import { sharedValidators } from "./shared";

function getCarLabel(car, index) {
  const desc = car?.description?.toString().trim();
  return desc ? `${desc}` : `Car #${index + 1}`;
}

const transportValidators = {
  // step 3 → transport type
  3: ({ step1 }, t) =>
    !step1.transport_type_id ? t("please_select_transport_type") : null,

  // step 4 → provider info
  4: ({ step1 }, t) => {
    if (!step1.is_provider) {
      // if (!step1.city_id) return t("please_fill_city");
      if (!step1.phone?.trim()) return t("please_fill_phone");
      if (!step1.address?.trim()) return t("please_fill_address");
    }
    return null;
  },

  // step 5 → availability
  5: ({ step1 }, t) => {
    const {
      availability_start,
      availability_end,
      day_start,
      day_end,
      transfers,
    } = step1;

    // Dates must exist
    // if (!availability_start) return t("please_select_availability_start");
    // if (!availability_end) return t("please_select_availability_end");

    // const startDate = dayjs(availability_start);
    // const endDate = dayjs(availability_end);

    // if (!startDate.isValid() || !endDate.isValid()) {
    //   return t("invalid_date_selection");
    // }

    // if (!startDate.isBefore(endDate)) {
    //   return t("availability_start_before_end");
    // }

    // Times must exist
    if (!day_start) return t("please_select_day_start");
    if (!day_end) return t("please_select_day_end");

    // Ensure end time > start time
    if (day_start >= day_end) {
      return t("day_end_must_be_after_start");
    }

    if (!transfers?.length) {
      return t("please_add_at_least_one_itinerary");
    }

    for (let i = 0; i < transfers.length; i++) {
      const tr = transfers[i];
      const idx = i + 1; // user-friendly index

      if (!tr.transfer_name?.trim()) {
        return t("itinerary_missing_name", { index: idx });
      }
      if (!tr.origin_terminal_type_id) {
        return t("itinerary_missing_origin_type", { index: idx });
      }
      if (!tr.origin_terminal_id) {
        return t("itinerary_missing_origin_terminal", { index: idx });
      }
      if (!tr.destination_terminal_type_id) {
        return t("itinerary_missing_destination_type", { index: idx });
      }
      if (!tr.destination_terminal_id) {
        return t("itinerary_missing_destination_terminal", { index: idx });
      }
      if (tr.origin_terminal_id === tr.destination_terminal_id) {
        return t("itinerary_same_origin_destination", { index: idx });
      }
      if (!tr.transfer_time || tr.transfer_time <= 0) {
        return t("itinerary_missing_duration", { index: idx });
      }
    }
    return null;
  },

  7: ({ step2 }, t) => {
    if (!step2.short_summary?.trim()) return t("please_fill_summary");
    if (step2.short_summary.trim().length < 32) return t("summary_too_short");
    if (!step2.detailed_description?.trim())
      return t("please_fill_description");
    if (step2.detailed_description.trim().length < 60)
      return t("description_too_short");
    console.log("Key points length:", step2?.key_points);
    if (step2?.key_points?.trim().length > 255)
      return t("key_points_too_long", { max: 255 });
    return null;
  },

  8: ({ step2 }, t) => {
    if (!step2.keyword.length) return t("please_select_keywords");
    return null;
  },

  9: ({ step2 }, t) => {
    if (!step2 || !Array.isArray(step2.cars) || step2.cars.length === 0) {
      return t("please_add_at_least_one_car");
    }

    for (let i = 0; i < step2.cars.length; i++) {
      const car = step2.cars[i];
      const carLabel = getCarLabel(car, i);

      // required ids
      if (!car.car_brand_id) {
        return `${t("car_missing_brand", { index: i + 1 })}`;
      }
      if (!car.car_model_id) {
        return `${t("car_missing_model", { index: i + 1 })}`;
      }
      if (!car.car_type_id) {
        return t("car_missing_type", { index: i + 1 });
      }

      // numeric checks
      if (
        !car.capacity_max ||
        car.capacity_max <= 0 ||
        car.capacity_max > 100
      ) {
        return `${t("car_invalid_capacity", { index: i + 1 })}`;
      }
      if (
        !car.luggage_capacity ||
        car.luggage_capacity <= 0 ||
        car.luggage_capacity > 50
      ) {
        return `${t("car_invalid_luggage_capacity", { index: i + 1 })}`;
      }
      if (!car.waiting_time || car.waiting_time <= 0) {
        return `${t("car_invalid_waiting_time", { index: i + 1 })}`;
      }

      // description
      if (!car.description || !car.description.toString().trim()) {
        return `${t("car_missing_description", { index: i + 1 })}`;
      }

      if (!Array.isArray(car.equipment_ids) || car.equipment_ids.length === 0) {
        return t("car_missing_equipments", { index: i + 1, car: carLabel });
      }
    }

    return null;
  },

  // Later steps should enforce images exist but tell the user to add them in Cars form

  11: ({ step2, step3 }, t) => {
    if (step3.cancellation_policy_id == null)
      return t("please_fill_cancellation_policy_id");
    return null;
  },

  13: ({ step1, step2, step3 }, t) => {
    const { pricing } = step3;
    if (!pricing?.length) {
      return t("please_add_prices_for_itineraries");
    }

    for (let i = 0; i < pricing.length; i++) {
      const tr = pricing[i];
      const itineraryIdx = i + 1;
      const itineraryName =
        step1.transfers?.[i]?.transfer_name || `#${itineraryIdx}`;

      if (!tr.prices?.length) {
        return t("itinerary_missing_prices", { itinerary: itineraryName });
      }

      for (let j = 0; j < tr.prices.length; j++) {
        const price = tr.prices[j];
        const carIdx = j + 1;
        const carId = price.car_id;
        const carName =
          step2.cars?.find((c) => c.id === carId)?.description ||
          `Car #${carIdx}`;

        if (!price.day_price || price.day_price <= 0) {
          return t("price_invalid_day", {
            itinerary: itineraryName,
            car: carName,
          });
        }
        if (!price.night_price || price.night_price <= 0) {
          return t("price_invalid_night", {
            itinerary: itineraryName,
            car: carName,
          });
        }
        if (price.is_place === undefined || price.is_place === null) {
          return t("price_missing_is_place", {
            itinerary: itineraryName,
            car: carName,
          });
        }
      }
    }

    return null;
  },

  12: ({ step2, step3 }, t) => {
    if (!step3.booking_confirmation_mode)
      return t("please_fill_booking_confirmation_mode");
    return null;
  },

  // shared validators
  shared: [
    sharedValidators.title,
    sharedValidators.keyPoints,
    sharedValidators.keywords,
    // sharedValidators.images,
    sharedValidators.cancellationPolicy,
    // sharedValidators.bookingConfirmation,
    // sharedValidators.applicableDays,
  ],
};

export default transportValidators;
